import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { ProductSelector } from 'apps-common/components/ProductSelector';
import { TermsDialog } from 'apps-common/components/TermsDialog';
import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { useGetProducts } from 'apps-common/hooks/useGetProducts';
import { BillingPeriod, FlowType } from 'apps-common/types';
import { track } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import { Flags, useFlag } from 'apps-common/utils/featureFlags';
import { getRatePlansFromProducts, ratePlansFromSubscription } from 'apps-common/utils/getProduct';
import { getDate } from 'apps-common/utils/helpers';
import { logger } from 'apps-common/utils/logger';
import { isExpiredSubscription } from 'apps-common/utils/member';
import { t } from 'translations';
import { Form, Header, Loader, SubmitButton } from 'ui';
import { MainContainer } from 'ui/styles/containers';
import { PageSubtitle, PageTitle } from 'ui/styles/text';

import { routes } from '../routes';
import { useStore } from '../store';

/* Usage:
- Renew flow for Pending Cancellation users (PM, Address exists)

- url: /select-plan
 */
export const RenewPendingSelectPlanPage = () => {
  const TITLE = t('membership_hub_product_page_title');
  const SUBTITLE = t('membership_hub_product_page_subtitle');
  const BADGE_TEXT = t('membership_planselector_badge_most_convenient');

  const { data: accountData, error, isError, isFetching } = useGetAccount();
  const {
    data: productsData,
    isFetching: isFetchingProducts,
    error: errorOnProducts,
  } = useGetProducts(accountData?.account?.shipToContact?.country);

  const currentSubscription = accountData?.account?.currentSubscription;

  const navigate = useNavigate();

  const setRatePlan = useStore((state) => state.setRatePlan);

  const showNewProductSelector = useFlag(Flags.HUB_NEW_PRODUCT_SELECTOR);
  const { formState, handleSubmit, register, watch } = useForm<{
    billingPeriod: BillingPeriod;
  }>({
    values: {
      billingPeriod: currentSubscription?.recurringFee?.billingPeriod ?? BillingPeriod.Years,
    },
  });

  if (isFetching || isFetchingProducts) {
    return (
      <>
        <Header appType="hub" hideBackButton onBackClick={() => navigate(routes.addressForm)} />
        <MainContainer>
          <Loader />
        </MainContainer>
      </>
    );
  }

  if (isError || !accountData) {
    throwError('errorOnGetAccount', error);
  }
  if (errorOnProducts || !productsData) {
    throwError('noProductFound', errorOnProducts);
  }

  if (!currentSubscription) {
    throwError('hubGeneralError', 'No subscription found for renewing user.');
  }

  // - todo: why is this file used for expiredSub renew flow ? (MMBER-10271)
  const isRenewFlowForExpiredSub = isExpiredSubscription(currentSubscription.subscriptionState);
  const isSubscriptionActive = !isRenewFlowForExpiredSub && currentSubscription.recurringFee; // Active subscription with no edge case

  const ratePlans = isSubscriptionActive
    ? ratePlansFromSubscription(currentSubscription)
    : getRatePlansFromProducts(productsData.products);

  const billingPeriod = watch('billingPeriod');
  const selectedPlan = ratePlans.find((rp) => rp.billingPeriod === billingPeriod);
  const nextPaymentDate = getDate(accountData.account.currentSubscription?.nextPaymentDate);

  const onSubmit = handleSubmit(({ billingPeriod }) => {
    track({
      event: 'CTA Clicked',
      payload: {
        cta: 'next',
        action: FlowType.AddOrUpdatePaymentMethod ? 'add_payment_method_next_step' : 'renew_next_step',
        step: 'plan_select',
      },
    });

    const ratePlan = ratePlans.find((rp) => rp.billingPeriod === billingPeriod)!;
    setRatePlan(ratePlan);

    track({
      event: 'Membership Plan Selected',
      payload: {
        productType: ratePlan.billingPeriod,
      },
    });

    logger.info(`Plan selected in renew flow, continuing to payment method page`);
    navigate(routes.reviewPaymentMethodForRenew);
  });

  return (
    <>
      <Header appType="hub" onBackClick={() => navigate(routes.index)} />
      <PageTitle>{TITLE}</PageTitle>
      <PageSubtitle data-testid="pageSubtitle">{SUBTITLE}</PageSubtitle>
      <MainContainer>
        <Form onSubmit={onSubmit}>
          <ProductSelector
            ratePlans={ratePlans}
            badgeText={BADGE_TEXT}
            showBadge={true}
            showNewProductSelector={showNewProductSelector}
            value={billingPeriod}
            nextPaymentDate={nextPaymentDate}
            {...register('billingPeriod', { required: true })}
          />

          {selectedPlan && (
            <TermsDialog simpleTerms={false} selectedPlan={selectedPlan} nextPaymentDate={nextPaymentDate} />
          )}
          <SubmitButton disabled={!formState.isValid}>{t('membership_signup_button_next')}</SubmitButton>
        </Form>
      </MainContainer>
    </>
  );
};
